import React, { useState, useEffect, useRef } from "react";
import {
  FaSearch,
  FaTimes,
  FaArrowLeft,
  FaPaperclip,
  FaMapPin,
  FaPlus,
} from "react-icons/fa";
import api from "../../services/api";
import Pusher from "pusher-js";
import "./chat.css";
import { useNotifications } from "../../context/NotificationContext";
import { useSearchParams } from "react-router-dom";

const PUSHER_KEY = "540d521fe5fe005cb99d";
const PUSHER_CLUSTER = "ap2";

const getDateLabel = (dateString) => {
  const messageDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const isToday =
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear();
  const isYesterday =
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear();
  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return messageDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const Chat = () => {
  const [admin, setAdmin] = useState(null);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [privatePartner, setPrivatePartner] = useState(null);
  const [activeTeam, setActiveTeam] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [typing, setTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTeams, setExpandedTeams] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [showMobileChat, setShowMobileChat] = useState(false);

  const [unread, setUnread] = useState({});
  const [lastActivity, setLastActivity] = useState({});
  const [conversationMeta, setConversationMeta] = useState({});
  const [msgSearch, setMsgSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);

  const messagesEndRef = useRef(null);
  const typingTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const token = localStorage.getItem("token");
  const { resetChat, playSound } = useNotifications();
  const [searchParams] = useSearchParams();
  const preselectedUser = searchParams.get("user");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
      if (window.innerWidth > 900) {
        setShowMobileChat(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { resetChat(); }, []);

  const handleSelectConversation = (partner, team) => {
    if (partner) {
      setPrivatePartner(partner);
      setActiveTeam(null);
    } else if (team) {
      setActiveTeam(team);
      setPrivatePartner(null);
    }
    if (isMobile) {
      setShowMobileChat(true);
    }
  };

  const handleBackToUsers = () => {
    setShowMobileChat(false);
    setActiveTeam(null);
    setPrivatePartner(null);
  };

  useEffect(() => {
    if (!isAdmin) {
      api.get("/user/chat_sidebar", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          setAdmin(res.data.admin);
          setTeams(res.data.teams || []);
          if (res.data.teams?.length) {
            setExpandedTeams({ [res.data.teams[0].id]: true });
          }
        })
        .catch(console.error);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/get_employees", { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setEmployees(res.data.employees || []);
          if (preselectedUser && res.data.employees) {
            const found = res.data.employees.find(u => u.id == preselectedUser);
            if (found) {
              setPrivatePartner(found);
              if (isMobile) setShowMobileChat(true);
            }
          }
        })
        .catch(console.error);
    }
  }, [isAdmin, preselectedUser]);

  useEffect(() => {
    if (!isAdmin && preselectedUser && preselectedUser !== "undefined") {
      let found = null;
      for (const team of teams) {
        if (team.members) {
          found = team.members.find(m => m.id == preselectedUser);
          if (found) break;
        }
      }
      if (!found && admin && admin.id == preselectedUser) {
        found = admin;
      }
      if (found) {
        setPrivatePartner(found);
        if (isMobile) setShowMobileChat(true);
      } else {
        api.get(`/user/get_user_info?user_id=${preselectedUser}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(res => {
            setPrivatePartner({
              id: parseInt(preselectedUser),
              employeeName: res.data.employeeName || "User",
            });
            if (isMobile) setShowMobileChat(true);
          })
          .catch(() => {
            setPrivatePartner({
              id: parseInt(preselectedUser),
              employeeName: "User",
            });
            if (isMobile) setShowMobileChat(true);
          });
      }
    }
  }, [isAdmin, preselectedUser, teams, admin, token]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      let url, params = {};
      if (activeTeam) {
        url = "/user/team/messages";
        params.team_id = activeTeam.id;
      } else if (privatePartner) {
        url = "/chat/messages";
        params.user_id = privatePartner.id;
      } else {
        setMessages([]);
        return;
      }
      const res = await api.get(url, { params, headers: { Authorization: `Bearer ${token}` } });
      const fetchedMessages = res.data.messages || [];
      setMessages(fetchedMessages);
      const lastMessage = fetchedMessages[fetchedMessages.length - 1];
      if (lastMessage) {
        const key = activeTeam ? `team_${activeTeam.id}` : `user_${privatePartner.id}`;
        updateConversationActivity(key, lastMessage, activeTeam ? activeTeam.name : privatePartner?.employeeName || "User");
      }
      setMsgSearch(""); setShowSearch(false); setSearchResults([]);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    if (privatePartner) setUnread((prev) => ({ ...prev, [`user_${privatePartner.id}`]: 0 }));
    if (activeTeam) setUnread((prev) => ({ ...prev, [`team_${activeTeam.id}`]: 0 }));
  }, [activeTeam, privatePartner]);

  const updateConversationActivity = (key, message, fallbackName = "User") => {
    const timestamp = message.created_at || new Date().toISOString();
    const senderName = message.sender_name || (message.sender_id == user.id ? "You" : fallbackName);
    const preview = message.file_path
      ? `${message.file_name || "File"}`
      : (message.message || "").trim().slice(0, 60);

    setLastActivity((prev) => ({ ...prev, [key]: timestamp }));
    setConversationMeta((prev) => ({
      ...prev,
      [key]: {
        senderName,
        preview,
        timestamp,
      },
    }));
  };

  useEffect(() => {
    let activeChannel;
    const pusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
    const privateChannel = pusher.subscribe("chat");

    privateChannel.bind("new-message", (newMsg) => {
      const currentUserId = user.id;
      if (newMsg.sender_id == currentUserId) return;
      let partnerId = newMsg.sender_id != currentUserId ? newMsg.sender_id : newMsg.receiver_id;
      if (!privatePartner || privatePartner.id != partnerId) {
        setUnread((prev) => ({ ...prev, [`user_${partnerId}`]: (prev[`user_${partnerId}`] || 0) + 1 }));
      }
      updateConversationActivity(`user_${partnerId}`, newMsg, "User");
    });

    privateChannel.bind("pin-updated", (data) => {
      const currentUserId = parseInt(user.id);
      const isRelevant = (data.sender_id === currentUserId || data.receiver_id === currentUserId);
      if (isRelevant) {
        setMessages((prev) =>
          prev.map((m) => m.id === data.message_id ? { ...m, pinned: data.pinned } : m)
        );
      }
    });

    if (privatePartner) {
      activeChannel = pusher.subscribe("chat");
      activeChannel.bind("new-message", (newMsg) => {
        const partnerId = privatePartner.id;
        if ((newMsg.sender_id == user.id && newMsg.receiver_id == partnerId) ||
          (newMsg.sender_id == partnerId && newMsg.receiver_id == user.id)) {
          const msgWithPin = { ...newMsg, pinned: newMsg.pinned !== undefined ? newMsg.pinned : 0 };
          setMessages((prev) => [...prev, msgWithPin]);
          updateConversationActivity(`user_${partnerId}`, newMsg, privatePartner?.employeeName || "User");
          if (newMsg.sender_id != user.id) playSound();
        }
      });
      activeChannel.bind("pin-updated", (data) => {
        const currentUserId = parseInt(user.id);
        const isRelevant = (data.sender_id === currentUserId || data.receiver_id === currentUserId);
        if (isRelevant) {
          setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, pinned: data.pinned } : m));
        }
      });
    } else if (activeTeam) {
      activeChannel = pusher.subscribe(`team-${activeTeam.id}`);
      activeChannel.bind("new-message", (newMsg) => {
        const msgWithPin = { ...newMsg, pinned: newMsg.pinned !== undefined ? newMsg.pinned : 0 };
        setMessages((prev) => [...prev, msgWithPin]);
        updateConversationActivity(`team_${activeTeam.id}`, newMsg, activeTeam?.name || "Team");
        if (newMsg.sender_id != user.id) playSound();
      });
      activeChannel.bind("pin-updated", (data) => {
        if (data.team_id == activeTeam.id) {
          setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, pinned: data.pinned } : m));
        }
      });
    }

    if (!isAdmin && teams.length > 0) {
      const teamChannels = teams.map((team) => {
        const ch = pusher.subscribe(`team-${team.id}`);
        ch.bind("new-message", (newMsg) => {
          if (newMsg.sender_id == user.id) return;
          if (!activeTeam || activeTeam.id != team.id) {
            setUnread((prev) => ({ ...prev, [`team_${team.id}`]: (prev[`team_${team.id}`] || 0) + 1 }));
          }
          updateConversationActivity(`team_${team.id}`, newMsg, team.name || "Team");
        });
        return ch;
      });
      return () => {
        teamChannels.forEach((ch) => { ch.unbind("new-message"); pusher.unsubscribe(ch.name); });
        if (activeChannel) { activeChannel.unbind("new-message"); activeChannel.unbind("pin-updated"); pusher.unsubscribe(activeChannel.name); }
        privateChannel.unbind("new-message"); privateChannel.unbind("pin-updated");
        pusher.unsubscribe("chat"); pusher.disconnect();
      };
    }
    return () => {
      if (activeChannel) { activeChannel.unbind("new-message"); activeChannel.unbind("pin-updated"); pusher.unsubscribe(activeChannel.name); }
      privateChannel.unbind("new-message"); privateChannel.unbind("pin-updated");
      pusher.unsubscribe("chat"); pusher.disconnect();
    };
  }, [activeTeam, privatePartner, user.id, isAdmin, teams]);

  const sendMessage = async () => {
    const text = newMsg.trim();
    const file = selectedFile;
    if (!text && !file) return;
    setSending(true);
    try {
      if (activeTeam) {
        const formData = new FormData();
        formData.append("team_id", activeTeam.id);
        if (file) { formData.append("file", file); formData.append("message", text); }
        else { formData.append("message", text); }
        await api.post("/user/team/send", formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": file ? "multipart/form-data" : "application/json" },
        });
        clearFileSelection();
      } else if (privatePartner) {
        const receiverId = privatePartner.id;
        if (file) {
          const formData = new FormData();
          formData.append("receiver_id", receiverId);
          formData.append("file", file);
          formData.append("message", text);
          await api.post("/chat/send_file", formData, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          });
          clearFileSelection();
        } else {
          await api.post("/chat/send", { receiver_id: receiverId, message: text }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }
      setNewMsg("");
    } catch (err) { setError(err.response?.data?.message || "Failed to send"); }
    finally { setSending(false); }
  };

  const clearFileSelection = () => { setSelectedFile(null); setFilePreview(""); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const handleFileSelect = (e) => { const file = e.target.files?.[0]; if (file) { setSelectedFile(file); setFilePreview(file.name); } };
  const handleCancelFile = () => clearFileSelection();
  const handleInputChange = (e) => { setNewMsg(e.target.value); };
  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const toggleTeamExpand = (teamId) => { setExpandedTeams((prev) => ({ ...prev, [teamId]: !prev[teamId] })); };

  const togglePinMessage = async (msgId) => {
    const message = messages.find(m => m.id === msgId);
    if (!message) return;
    const newPinnedStatus = message.pinned ? 0 : 1;
    try {
      const endpoint = activeTeam ? "/user/team_toggle_pin" : "/chat/toggle_pin";
      await api.put(endpoint, { message_id: msgId, pinned: newPinnedStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, pinned: newPinnedStatus } : m));
    } catch (err) { setError(err.response?.data?.message || "Failed to toggle pin"); }
  };

  const handleMessageSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setMsgSearch(term);
    if (!term.trim()) { setSearchResults([]); return; }
    const results = [];
    messages.forEach((msg, idx) => {
      if (msg.message && msg.message.toLowerCase().includes(term)) results.push({ ...msg, idx });
    });
    setSearchResults(results); setCurrentSearchIndex(0);
    if (results.length > 0) scrollToMessage(results[0].id);
  };

  const scrollToMessage = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("msg-highlight"); setTimeout(() => el.classList.remove("msg-highlight"), 2000); }
  };

  const navigateSearch = (direction) => {
    if (searchResults.length === 0) return;
    let newIndex = currentSearchIndex + direction;
    if (newIndex < 0) newIndex = searchResults.length - 1;
    if (newIndex >= searchResults.length) newIndex = 0;
    setCurrentSearchIndex(newIndex); scrollToMessage(searchResults[newIndex].id);
  };

  const getPinnedMessages = () => messages.filter((msg) => msg.pinned === 1);
  const pinnedMsgs = getPinnedMessages();

  const getConversationPreview = (key) => {
    const meta = conversationMeta[key];
    if (!meta) return "";
    return meta.preview ? `${meta.senderName}: ${meta.preview}` : meta.senderName;
  };

  const sortByActivity = (list, prefix) => {
    return [...list].sort((a, b) => {
      const timeA = lastActivity[`${prefix}${a.id}`] || conversationMeta[`${prefix}${a.id}`]?.timestamp || 0;
      const timeB = lastActivity[`${prefix}${b.id}`] || conversationMeta[`${prefix}${b.id}`]?.timestamp || 0;
      return new Date(timeB) - new Date(timeA);
    });
  };

  const filteredEmployees = employees.filter((emp) => emp.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()));
  const sortedEmployees = sortByActivity(filteredEmployees, "user_");

  const filteredTeams = teams
    .filter((team) => {
      const nameMatch = team.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const memberMatch = team.members?.some((m) => m.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()));
      return nameMatch || memberMatch;
    })
    .map((team) => {
      const members = team.members || [];
      const filteredMembers = searchQuery
        ? members.filter((m) => m.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) || m.role?.toLowerCase().includes(searchQuery.toLowerCase()))
        : members;
      return { ...team, members: filteredMembers };
    });
  const sortedTeams = sortByActivity(filteredTeams, "team_");

  const renderUnreadBadge = (key) => {
    const count = unread[key] || 0;
    if (count === 0) return null;
    return <span className="chat-unread-badge">{count}</span>;
  };

  const renderMessage = (msg) => {
    const isPinned = msg.pinned === 1;
    const sender = msg.sender_name || (msg.sender_id == user.id ? "You" : "User");
    return (
      <div key={msg.id} id={`msg-${msg.id}`} className={`message ${msg.sender_id == user.id ? "sent" : "received"} ${isPinned ? "pinned" : ""}`}>
        <div className="msg-header">
          <small className="sender">{sender}</small>
          <button className={`pin-btn ${isPinned ? "active" : ""}`} onClick={() => togglePinMessage(msg.id)} title={isPinned ? "Unpin" : "Pin"} aria-label={isPinned ? "Unpin message" : "Pin message"}>
            <FaMapPin />
          </button>
        </div>
        {msg.message && <p>{msg.message}</p>}
        {msg.file_path && (
          <a href={`https://dt-attendance-system.desired-techs.com/dt-backend/${msg.file_path}`} target="_blank" rel="noreferrer" className="chat-file-link" download={msg.file_name || msg.file_path.split("/").pop()}>
            {msg.file_name || msg.file_path.split("/").pop()}
          </a>
        )}
        <span className="time">{new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
    );
  };

  const renderSidebar = () => (
    <div className={`chat-sidebar ${isMobile && showMobileChat ? 'mobile-hidden' : ''}`}>
      <div className="sidebar-header"><h3>Chats</h3></div>
      <div className="sidebar-search">
        <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
      </div>
      <div className="user-list-vertical">
        {admin && (searchQuery === "" || admin.employeeName?.toLowerCase().includes(searchQuery.toLowerCase())) && (
          <div className={`user-item ${privatePartner?.id === admin.id && !activeTeam ? "active" : ""}`} onClick={() => handleSelectConversation(admin, null)}>
            <div className="user-avatar-small">A</div>
            <div className="user-info">
              <span className="user-name">Admin</span>
              <span className="conversation-preview">{getConversationPreview(`user_${admin.id}`) || "Tap to start chatting"}</span>
            </div>
            {renderUnreadBadge(`user_${admin.id}`)}
          </div>
        )}
        {sortedTeams.map((team) => (
          <React.Fragment key={team.id}>
            <div className={`user-item ${activeTeam?.id === team.id ? "active" : ""}`} onClick={() => handleSelectConversation(null, team)}>
              <div className="user-avatar-small">T</div>
              <div className="user-info">
                <span className="user-name">{team.name}</span>
                <span className="conversation-preview">{getConversationPreview(`team_${team.id}`) || `${team.members.length} members`}</span>
              </div>
              {renderUnreadBadge(`team_${team.id}`)}
              <button className="expand-btn" onClick={(e) => { e.stopPropagation(); toggleTeamExpand(team.id); }}>{expandedTeams[team.id] ? "▼" : "▶"}</button>
            </div>
            {expandedTeams[team.id] && (
              <div className="sub-members">
                {sortByActivity(team.members, "user_").map((member) => (
                  <div key={member.id} className={`user-item sub-member ${privatePartner?.id === member.id && !activeTeam ? "active" : ""}`} onClick={() => handleSelectConversation(member, null)}>
                    <div className="user-avatar-small">{member.employeeName?.charAt(0) || "?"}</div>
                    <div className="user-info">
                      <span className="user-name">{member.employeeName}</span>
                      <span className="conversation-preview">{getConversationPreview(`user_${member.id}`) || member.role}</span>
                    </div>
                    {renderUnreadBadge(`user_${member.id}`)}
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderChatArea = () => (
    <div className={`chat-main ${isMobile && !showMobileChat ? 'mobile-hidden' : ''}`}>
      {activeTeam || privatePartner ? (
        <div className="chat-container">
          {/* Header with search inline */}
          <div className="chat-header">
            <button className="back-btn" onClick={handleBackToUsers}>
              <span className="back-arrow"><FaArrowLeft /></span>
              <span className="back-text">Back</span>
            </button>
            <span className="chat-header-title">{activeTeam ? `# ${activeTeam.name}` : privatePartner?.employeeName}</span>
            <button className="search-toggle-btn chat-header-search" onClick={() => { setShowSearch(!showSearch); if (!showSearch) setTimeout(() => searchInputRef.current?.focus(), 100); }}>
              <FaSearch />
            </button>
          </div>

          {/* Search bar (collapsible) */}
          {showSearch && (
            <div className="msg-search-bar">
              <input type="text" placeholder="Search messages..." value={msgSearch} onChange={handleMessageSearch} ref={searchInputRef} autoFocus />
              {searchResults.length > 0 && <span className="search-count">{currentSearchIndex + 1}/{searchResults.length}</span>}
              <button className="search-nav-btn" onClick={() => navigateSearch(-1)}>▲</button>
              <button className="search-nav-btn" onClick={() => navigateSearch(1)}>▼</button>
              <button className="search-close-btn" onClick={() => { setShowSearch(false); setMsgSearch(""); setSearchResults([]); }} aria-label="Close message search">
                <FaTimes />
              </button>
            </div>
          )}

          {/* Pinned messages */}
          {pinnedMsgs.length > 0 && (
            <div className="pinned-messages">
              <div className="pinned-header"><span><FaMapPin /></span> Pinned ({pinnedMsgs.length})</div>
              <div className="pinned-scroll">
                {pinnedMsgs.map((msg) => (
                  <div key={`pinned-${msg.id}`} className="pinned-msg" onClick={() => scrollToMessage(msg.id)}>
                    <span className="pinned-msg-sender">{msg.sender_name || (msg.sender_id == user.id ? "You" : "User")}</span>
                    <span className="pinned-msg-text">{msg.file_path ? `${msg.file_name || msg.file_path.split('/').pop()}` : msg.message?.substring(0, 60)}{!msg.file_path && msg.message?.length > 60 ? "…" : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages">
            {loading && <div className="loading-spinner"></div>}
            {messages.map((msg) => renderMessage(msg))}
            {typing && <div className="typing-indicator">Typing...</div>}
            <div ref={messagesEndRef} />
          </div>

          {/* File preview */}
          {selectedFile && (
            <div className="file-preview-bar">
              <span><FaPaperclip style={{ marginRight: "6px" }} />{filePreview}</span>
              <button onClick={handleCancelFile} disabled={sending} aria-label="Remove attachment"><FaTimes /></button>
            </div>
          )}

          {/* Input area - compact like chat apps */}
          <div className="chat-input">
            <button className="btn-attach" onClick={() => fileInputRef.current?.click()} disabled={sending} title="Attach file"><FaPaperclip /></button>
            <input type="text" placeholder="Type a message..." value={newMsg} onChange={handleInputChange} onKeyDown={handleKeyDown} disabled={sending} />
            <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileSelect} />
            {sending ? (
              <span className="uploading-text">...</span>
            ) : (
              <button onClick={sendMessage} className="btn-send" disabled={!newMsg.trim() && !selectedFile}>
                {/* <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg> */}
                Send
              </button>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
      ) : (
        <div className="no-chat-selected"><div className="no-chat-icon">💬</div><p>Select a conversation to start chatting</p></div>
      )}
    </div>
  );

  if (isAdmin) {
    return (
      <div className="admin-container">
        <div className="chat-admin-layout">
          <div className={`chat-sidebar ${isMobile && showMobileChat ? 'mobile-hidden' : ''}`}>
            <div className="sidebar-header"><h3>Employees</h3></div>
            <div className="sidebar-search"><input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></div>
            <div className="user-list-vertical">
              {sortedEmployees.map((emp) => (
                <div key={emp.id} className={`user-item ${privatePartner?.id === emp.id ? "active" : ""}`} onClick={() => handleSelectConversation(emp, null)}>
                  <div className="user-avatar-small">{emp.employeeName?.charAt(0) || "E"}</div>
                  <div className="user-info">
                    <span className="user-name">{emp.employeeName}</span>
                    <span className="conversation-preview">{getConversationPreview(`user_${emp.id}`) || emp.role}</span>
                  </div>
                  {renderUnreadBadge(`user_${emp.id}`)}
                </div>
              ))}
            </div>
          </div>
          {renderChatArea()}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="chat-admin-layout">
        {renderSidebar()}
        {renderChatArea()}
      </div>
    </div>
  );
};

export default Chat;